import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/utils';
import type { Block, Inline } from './markdown';

// Renders the parsed article blocks as React elements (React escapes all text,
// so there is no HTML injection path). Typography is plain Tailwind on the
// design tokens, so it follows light and dark with the rest of the app.

/** An internal link goes through the router (so HashRouter and BrowserRouter both
 * work); an https link opens in a new tab; anything else is shown as plain text. */
function InlineLink({ href, children }: { href: string; children: ReactNode }) {
  const cls = 'font-medium text-primary-ink underline underline-offset-2 hover:text-primary-hover';
  if (href.startsWith('/help/') || href === '/help') {
    return (
      <Link to={href} className={cls}>
        {children}
      </Link>
    );
  }
  if (/^https:\/\//i.test(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
        {children}
      </a>
    );
  }
  return <>{children}</>;
}

export function renderInline(nodes: Inline[]): ReactNode {
  return nodes.map((node, i) => {
    if (node.type === 'text') return <Fragment key={i}>{node.text}</Fragment>;
    if (node.type === 'bold') {
      return (
        <strong key={i} className="font-semibold text-ink">
          {renderInline(node.children)}
        </strong>
      );
    }
    return (
      <InlineLink key={i} href={node.href}>
        {renderInline(node.children)}
      </InlineLink>
    );
  });
}

/** A `> Note: ...` line: the "Note:" lead-in becomes a bold label. */
function NoteBody({ children }: { children: Inline[] }) {
  const first = children[0];
  if (first?.type === 'text') {
    const m = /^(Note|Tip|Warning|Caution)\s*:\s*/i.exec(first.text);
    if (m) {
      const rest: Inline[] = [
        { type: 'text', text: first.text.slice(m[0].length) },
        ...children.slice(1),
      ];
      return (
        <>
          <strong className="font-semibold">{m[1]}:</strong> {renderInline(rest)}
        </>
      );
    }
  }
  return <>{renderInline(children)}</>;
}

export function MarkdownView({ blocks, className }: { blocks: Block[]; className?: string }) {
  return (
    <div className={cn('max-w-[68ch] text-[15px] leading-7 text-ink', className)}>
      {blocks.map((b, i) => {
        switch (b.type) {
          case 'h2':
            return (
              <h2
                key={i}
                id={b.anchor}
                className="mb-3 mt-10 scroll-mt-20 font-serif text-xl font-semibold leading-7 tracking-tight first:mt-0"
              >
                {b.text}
              </h2>
            );
          case 'h3':
            return (
              <h3 key={i} id={b.anchor} className="mb-2 mt-6 scroll-mt-20 text-base font-semibold">
                {b.text}
              </h3>
            );
          case 'p':
            return (
              <p key={i} className="my-3">
                {renderInline(b.children)}
              </p>
            );
          case 'ol':
            return (
              <ol
                key={i}
                className="my-3 list-decimal space-y-2 pl-6 marker:font-semibold marker:text-ink-3"
              >
                {b.items.map((item, j) => (
                  <li key={j} className="pl-1">
                    {renderInline(item)}
                  </li>
                ))}
              </ol>
            );
          case 'ul':
            return (
              <ul key={i} className="my-3 list-disc space-y-1.5 pl-6 marker:text-ink-3">
                {b.items.map((item, j) => (
                  <li key={j} className="pl-1">
                    {renderInline(item)}
                  </li>
                ))}
              </ul>
            );
          case 'note':
            return (
              <aside
                key={i}
                className="my-4 rounded-md border border-l-4 border-line border-l-primary bg-primary-soft px-4 py-3 text-sm leading-6 text-primary-ink"
              >
                <NoteBody>{b.children}</NoteBody>
              </aside>
            );
        }
      })}
    </div>
  );
}
